import type { RecentlyDrunkResponse, RecentlyDrunkRow } from "@corklog/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { fetchRecentlyDrunk } from "../api.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";

/**
 * *Drucket* — the bottles most recently recorded as drunk, the latest first.
 *
 * A plain list on purpose. *Statistik* already answers what was drunk by the
 * year and *Anteckningar* what was said about it; this is the third question,
 * *what did we open last*, and its answer is a row per bottle in the order it
 * happened. Nothing is grouped by month: the day is printed on every row, and a
 * heading per month would be a second thing to read for a list nobody scrolls
 * past a screen of.
 *
 * The day is the day it was drunk, not the day it was recorded — see
 * `routes/drunk.ts`.
 */
type State =
  | { status: "loading" }
  | { status: "ready"; list: RecentlyDrunkResponse }
  | { status: "failed" };

export function Drunk() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    void fetchRecentlyDrunk(cellarId)
      .then((list) => setState({ status: "ready", list }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="drunk" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("drunk.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("drunk.loadFailed")}
        </p>
      )}

      {state.status === "ready" &&
        (state.list.bottles.length === 0 ? (
          <p className="text-muted mt-8 max-w-prose text-sm">{t("drunk.empty")}</p>
        ) : (
          <>
            <p className="text-muted mt-6 text-sm">
              {state.list.bottles.length >= state.list.limit
                ? t("drunk.limited", { count: state.list.limit })
                : t("drunk.count", { count: state.list.bottles.length })}
            </p>
            <ul className="border-line mt-3 border-t">
              {state.list.bottles.map((row) => (
                <DrunkRow key={row.eventId} cellarId={cellarId} row={row} />
              ))}
            </ul>
          </>
        ))}
    </section>
  );
}

/** A day as this application writes days. Parsed as UTC and printed as UTC:
 *  the server sends a date with no time in it, and a local-time conversion
 *  would move it across midnight. */
function longDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(locale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function DrunkRow({ cellarId, row }: { cellarId: string; row: RecentlyDrunkRow }) {
  const { t } = useTranslation();

  // The same two states the wine list prints for a year, so a wine reads the
  // same wherever it appears.
  const year = row.isNonVintage
    ? t("wines.nvShort")
    : row.vintage === null
      ? ""
      : String(row.vintage);

  return (
    <li className="border-line border-b py-4">
      <div className="grid grid-cols-[2.75rem_1fr] gap-x-4">
        <div className="font-display text-muted pt-0.5 text-right text-lg">
          {row.wineNumber}
        </div>
        <div className="min-w-0">
          <h3 className="font-display truncate text-lg leading-tight">
            <Link
              to={`/kallare/${cellarId}/viner/${row.wineId}`}
              className="underline-offset-4 hover:underline"
            >
              {row.wineName}
            </Link>
          </h3>
          <p className="text-muted mt-0.5 truncate text-sm">
            {row.producer}
            {year !== "" && <>, {year}</>}
            {/* A bottle is the usual format and need not be said; a magnum
                is a different object and always is. */}
            {row.format !== "Bottle" && <> · {t(`format.${row.format}`)}</>}
          </p>
          <p className="text-muted mt-1 text-sm">
            {longDay(row.drunkOn)}
            {row.recordedBy !== null && (
              <> · {t("drunk.recordedBy", { name: row.recordedBy })}</>
            )}
          </p>
        </div>
      </div>
    </li>
  );
}
